export interface IInvokeExecutorRequest {
  executorName: string;
  requestId?: string;
  sessionId?: string;
  inputVariables?: Record<string, any>;
}

export interface IExecutionRatingDto {
  _id?: string;
  userId: string;
  rating: number;
  ratingReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export enum ExecutorExecutionStatus {
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface IExecutionDetailDto<T = unknown> {
  executorName: string;
  sessionId: string;
  requestId: string;
  createdAt: string;
  status: ExecutorExecutionStatus;
  duration: number | null;
  result: T;
  error: string | null;
  ratings: IExecutionRatingDto[];
  inputVariables: Record<string, unknown> | null;
}

export interface IChatV2ExecutorResult {
  response?: string; // holds pre-generative UI responses
  citations: Record<string, any>;
  contentBlocks?: IChatV2ContentItem[];
}

export type ExecutionSystemEvent =
  | { type: 'executor-start'; data: { timestamp: string; requestId: string; sessionId: string } }
  | { type: 'keep-alive'; timestamp: string }
  | { type: 'executor-success'; execution: IExecutionDetailDto }
  | { type: 'executor-failure'; message: string; code: string };

export type ExecutionUserEvent<C = string, R = unknown> = { type: 'chunk'; data: C } | { type: 'streaming-finished'; result: R };

export type ExecutionStreamEvent<C = string, R = unknown> = ExecutionSystemEvent | ExecutionUserEvent<C, R>;

export interface IChatV2ExecutionEvent {
  id: string;
  annotation?: string;
}

export interface IChatV2ComponentChunk {
  componentType: string;
  data: Record<string, unknown>;
}

export type IChatV2ContentItem = { type: 'text'; text: string } | ({ type: 'ui-component' } & IChatV2ComponentChunk);

export type IChatV2ChunkData =
  | { type: 'text'; text: string }
  | ({ type: 'tool-call' } & IChatV2ExecutionEvent)
  | ({ type: 'ui-component' } & IChatV2ComponentChunk)
  | { type: 'session-title'; title: string };

export type IChatV2StreamEvent = ExecutionStreamEvent<IChatV2ChunkData, IChatV2ExecutorResult>;

export const SSE_CHAT_V2_EVENT_TYPES = {
  text: 'text',
  'tool-call': 'tool-call',
  'ui-component': 'ui-component',
  'session-title': 'session-title',
} as const satisfies Record<IChatV2ChunkData['type'], IChatV2ChunkData['type']>;

export const SSE_EVENT_TYPES = {
  'executor-start': 'executor-start',
  chunk: 'chunk',
  'keep-alive': 'keep-alive',
  'streaming-finished': 'streaming-finished',
  'executor-success': 'executor-success',
  'executor-failure': 'executor-failure',
} as const satisfies Record<ExecutionStreamEvent['type'], ExecutionStreamEvent['type']>;
